import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { Users, Eye, Filter, Search } from 'lucide-react';

import { onboardingCasesAPI } from '@/shared/lib/api';
import { useDebounce } from '@/shared/hooks/useDebounce';
import {
  OnboardingStage,
  STAGE_LABELS,
  STAGE_ORDER,
  STAGE_COLORS,
  type OnboardingCase,
  type ListCasesResponse,
  type StatsResponse,
} from '../types';

const PAGE_SIZE = 50;

/**
 * Lista de casos de onboarding (novo módulo client-onboarding).
 *
 * Rota: `/onboarding` (protegida por CLIENT_OPERATIONS).
 * Layout: header + stats por stage + filtros (chips + search) + tabela.
 */
export function ClientOnboardingListPage() {
  const [stageFilter, setStageFilter] = useState<OnboardingStage | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounced = useDebounce(searchInput, 400);
  const [page, setPage] = useState(0);

  // Reset page ao mudar filtros
  useEffect(() => {
    setPage(0);
  }, [stageFilter, searchDebounced]);

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['onboarding-cases', 'stats'],
    queryFn: async () => {
      const response = await onboardingCasesAPI.getStats();
      return response.data.data as StatsResponse;
    },
    staleTime: 60 * 1000,
  });

  const {
    data: casesData,
    isLoading: isLoadingCases,
    isFetching,
  } = useQuery({
    queryKey: ['onboarding-cases', 'list', stageFilter, searchDebounced, page],
    queryFn: async () => {
      const params: Record<string, string | number | undefined> = {
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
      };
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (searchDebounced) params.search = searchDebounced;
      const response = await onboardingCasesAPI.list(params);
      return response.data.data as ListCasesResponse;
    },
    staleTime: 30 * 1000,
  });

  const cases = casesData?.cases ?? [];
  const total = casesData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const chipCounts = useMemo(() => {
    const byStage = stats?.byStage ?? ({} as Record<OnboardingStage, number>);
    return byStage;
  }, [stats]);

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoadingStats && isLoadingCases) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-72 bg-muted rounded" />
          <div className="h-4 w-96 bg-muted rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded" />
            ))}
          </div>
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-7 w-7 text-primary" />
            Onboarding de Clientes
          </h1>
          <p className="text-muted-foreground mt-1">
            Acompanhe cada cliente na jornada do novo fluxo de onboarding.
          </p>
        </div>
      </div>

      {/* KPI cards resumidos */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats?.total ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Total de casos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-700">
              {(chipCounts.AWAITING_FORM_2 ?? 0) + (chipCounts.FORM_1_COMPLETED ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando Form 2</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-teal-700">
              {chipCounts.CALL_SCHEDULED ?? 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Call agendada</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-700">{chipCounts.FAILED ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Com falha</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros: chips por stage */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Filtrar por etapa</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setStageFilter('all')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              stageFilter === 'all'
                ? 'bg-primary text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Todos ({stats?.total ?? 0})
          </button>
          {STAGE_ORDER.map((stage) => {
            const count = chipCounts[stage] ?? 0;
            const isActive = stageFilter === stage;
            return (
              <button
                key={stage}
                type="button"
                onClick={() => setStageFilter(stage)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {STAGE_LABELS[stage]} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder="Buscar por nome, e-mail, telefone ou CPF..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Casos
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              {isFetching ? 'Atualizando...' : `${total} caso(s)`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-1">Nenhum caso encontrado</h3>
              <p className="text-sm text-muted-foreground">
                Ajuste os filtros para ver casos existentes.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Atualizado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map((c: OnboardingCase) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium text-foreground">{c.clientName}</div>
                      {c.clientCpf && (
                        <div className="text-xs text-muted-foreground font-mono">
                          CPF: {c.clientCpf}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.clientEmail && <div className="text-sm">{c.clientEmail}</div>}
                      {c.clientPhone && (
                        <div className="text-xs text-muted-foreground">{c.clientPhone}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={STAGE_COLORS[c.currentStage]}
                      >
                        {STAGE_LABELS[c.currentStage]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(c.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(c.updatedAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/onboarding/${c.id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação simples */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Helpers locais
// ------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
